import { useMemo, useState } from 'react'

const API_BASE = import.meta.env.DEV ? '' : import.meta.env.BASE_URL.replace(/\/$/, '')

function buildShareUrl(token: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '')
  return `${window.location.origin}${base}/share/${token}`
}

function reportCopy(token: string): void {
  try {
    const url = `${API_BASE}/api/share/${encodeURIComponent(token)}/copy`
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url)
    } else {
      void fetch(url, { method: 'POST', keepalive: true }).catch(() => {})
    }
  } catch {
    /* ignore */
  }
}

function ShareIcon() {
  // Standard "share / upload" glyph — square with arrow pointing up.
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  )
}

export function ShareIconButton({ handoffToken }: { handoffToken: string | undefined }) {
  const [copied, setCopied] = useState(false)
  const shareUrl = useMemo(
    () => (handoffToken ? buildShareUrl(handoffToken) : ''),
    [handoffToken]
  )

  if (!handoffToken) return null

  const onClick = async () => {
    // Prefer native share on mobile (Messages, WhatsApp, etc.).
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'My TDEE result',
          text: 'Here’s my AI-calculated TDEE — calculate yours:',
          url: shareUrl,
        })
        reportCopy(handoffToken)
        return
      } catch {
        // User cancelled the share sheet — fall through to clipboard copy.
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl)
      reportCopy(handoffToken)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard blocked — silent fail */
    }
  }

  return (
    <span className="share-icon-wrap">
      <button
        type="button"
        className="share-icon-btn"
        onClick={onClick}
        title="Share this result"
        aria-label="Share this result"
      >
        <ShareIcon />
      </button>
      {copied && <span className="share-icon-toast">Link copied</span>}
    </span>
  )
}
